"use client"

/**
 * The review step's classification row — the model's Type / Priority / Area
 * guesses, correctable before the ticket is sent.
 *
 * #691 — this used to be three equal columns with the shared Select trigger's
 * `w-fit whitespace-nowrap` defaults: a trigger sized to its content cannot
 * shrink, so "Change Request" / "Removal Request" spilled out of its ~108px
 * column and drew over Priority. Type and Priority now share a two-column row,
 * Area takes the full width below, and every control fills — and may shrink to
 * — its own cell (`w-full min-w-0`, cells `min-w-0`), truncating rather than
 * overlapping if a label is ever longer still.
 */
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { categoryLabels } from "@/lib/feedback/config"
import { useTranslation } from "@/lib/i18n"
import { categoryText, priorityText } from "./feedback-labels"

interface FeedbackClassificationFieldsProps {
  category: string
  onCategoryChange: (value: string) => void
  priority: string
  onPriorityChange: (value: string) => void
  area: string
  onAreaChange: (value: string) => void
}

export function FeedbackClassificationFields({
  category,
  onCategoryChange,
  priority,
  onPriorityChange,
  area,
  onAreaChange,
}: FeedbackClassificationFieldsProps) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="min-w-0 space-y-1">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("common.type")}
        </label>
        <Select value={category} onValueChange={onCategoryChange}>
          <SelectTrigger className="h-7 w-full min-w-0 text-[11px]" aria-label={t("feedbackTickets.fields.category")}>
            <SelectValue placeholder={t("common.type")} />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(categoryLabels).map((value) => (
              <SelectItem key={value} value={value} className="text-xs">
                {categoryText(t, value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-0 space-y-1">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("feedbackTickets.fields.priority")}
        </label>
        <Select value={priority} onValueChange={onPriorityChange}>
          <SelectTrigger className="h-7 w-full min-w-0 text-[11px]" aria-label={t("feedbackTickets.fields.priority")}>
            <SelectValue placeholder={t("feedbackTickets.fields.priority")} />
          </SelectTrigger>
          <SelectContent>
            {["Low", "Medium", "High"].map((p) => (
              <SelectItem key={p} value={p} className="text-xs">
                {priorityText(t, p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 min-w-0 space-y-1">
        <label
          htmlFor="feedback-widget-area"
          className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {t("feedbackTickets.fields.area")}
        </label>
        <Input
          id="feedback-widget-area"
          value={area}
          onChange={(e) => onAreaChange(e.target.value)}
          placeholder={t("feedbackTickets.classification.areaPlaceholder")}
          className="h-7 text-[11px]"
          maxLength={255}
        />
      </div>
    </div>
  )
}
